const ZENDESK_KEY = import.meta.env.VITE_ZENDESK_WIDGET_KEY as
  | string
  | undefined;
const ZENDESK_SCRIPT_ID = "ze-snippet";

type LoginCallback = (jwt: string) => void;

let readyPromise: Promise<boolean> | null = null;

/**
 * Inject the Zendesk Web Widget snippet once. Resolves `true` when the script
 * has loaded (so commands can be queued), `false` if the key is missing or the
 * network request fails — callers stay no-ops instead of throwing.
 */
export const loadZendesk = (): Promise<boolean> => {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise<boolean>((resolve) => {
    if (!ZENDESK_KEY) {
      if (import.meta.env.DEV)
        console.warn(
          "VITE_ZENDESK_WIDGET_KEY is not set, support widget disabled",
        );
      return resolve(false);
    }

    if (document.getElementById(ZENDESK_SCRIPT_ID)) return resolve(true);

    try {
      const script = document.createElement("script");
      script.id = ZENDESK_SCRIPT_ID;
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        console.error("Failed to load the Zendesk widget script");
        resolve(false);
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error("Error injecting the Zendesk widget script:", error);
      resolve(false);
    }
  });

  return readyPromise;
};

/**
 * Run a widget command once it is ready, swallowing errors so a single failing
 * call never cascades and breaks the app (mirrors tracking's `safe()` wrapper).
 */
const withZendesk = async (
  run: (zE: NonNullable<Window["zE"]>) => void,
): Promise<void> => {
  const ready = await loadZendesk();
  if (!ready || !window.zE) return;
  try {
    run(window.zE);
  } catch (error) {
    console.error("Zendesk command failed:", error);
  }
};

export const openZendesk = () => withZendesk((zE) => zE("messenger", "open"));

export const hideZendesk = () => withZendesk((zE) => zE("messenger", "hide"));

export const showZendesk = () => withZendesk((zE) => zE("messenger", "show"));

export const logoutZendeskUser = () =>
  withZendesk((zE) => zE("messenger", "logoutUser"));

/**
 * Authenticate the user into the conversation. `getToken` is invoked by the
 * widget whenever it needs a (fresh) JWT, including on re-auth.
 */
export const loginZendeskUser = (getToken: () => Promise<string | null>) =>
  withZendesk((zE) =>
    zE("messenger", "loginUser", async (callback: LoginCallback) => {
      const token = await getToken();
      if (token) callback(token);
    }),
  );

/** Attach context (platform, app version, VIP status) to the conversation. */
export const setZendeskTags = (tags: Array<string>) =>
  withZendesk((zE) => zE("messenger:set", "conversationTags", tags));

/**
 * Attach typed key/value metadata (ticket fields) to the conversation. `id` is
 * the numeric Zendesk field ID (see `ZENDESK_FIELD_IDS` in `zendeskFields.ts`).
 */
export const setZendeskFields = (
  fields: Array<{ id: string; value: string }>,
) => withZendesk((zE) => zE("messenger:set", "conversationFields", fields));
